import type { LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import BackButton from '@/components/ui/BackButton'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

interface LearnHeadProps {
  /** The page's own name, its one visible `<h1>` (the strip above names the section, not the page). */
  title: string
  /** The parent page; drawn as the kit's back button before the title. A word page has none. */
  back?: NonNullable<LinkProps['to']>
  /** "Step 1 of 7": a small line over the title, under the title's own words. */
  kicker?: ReactNode
  /** One line under the title. */
  sub?: ReactNode
  /** The right end: a step's progress (sat on the sub line), a word's search (level with the title). */
  aside?: ReactNode
  /** Where the aside sits: on the head's last line (`end`), or level with the title (`start`). */
  asideAlign?: 'start' | 'end'
}

/**
 * rest:ln — the head of a Learn drill-down and of Objectives (mock `ln-head`): unlike a hub page
 * under the strip, whose `TitleRow` keeps its `h1` for screen readers only, these pages draw their
 * title (owner L2: the strip stays above it, the parent tab lit). The kicker and the sub line hang
 * under the title's words, past the back button and its 8 gap (mock: 32; the kit's back is 32 wide).
 * The head shares the strip's wrapper, which `LEARN_PAGE` pulls to 16 over the first block; `pb-2`
 * makes that the mock's 24 from the head to it.
 */
export default function LearnHead({
  title,
  back,
  kicker,
  sub,
  aside,
  asideAlign = 'end',
}: LearnHeadProps) {
  const indent = back === undefined ? null : 'pl-10'
  return (
    <div
      className={cn(
        'flex flex-col gap-4 pb-2 @3xl:flex-row @3xl:justify-between @3xl:gap-6',
        asideAlign === 'end' ? '@3xl:items-end' : '@3xl:items-start',
      )}
    >
      <div className="flex min-w-0 flex-col">
        {kicker === undefined ? null : (
          <p className={cn('text-body-xs text-muted-foreground-subtle', indent)}>{kicker}</p>
        )}
        <div className="flex items-center gap-2">
          {back === undefined ? null : <BackButton to={back} />}
          <h1 className="min-w-0 text-h-2xl text-foreground">{title}</h1>
        </div>
        {sub === undefined ? null : (
          <p className={cn('pt-2 text-body-m text-muted-foreground', indent)}>{sub}</p>
        )}
      </div>
      {aside === undefined ? null : <div className="shrink-0">{aside}</div>}
    </div>
  )
}

/** `LearnHead` while its page loads: the title's line (and the back button's room), the sub line. */
export function LearnHeadSkeleton({ back = false, sub = true }: { back?: boolean; sub?: boolean }) {
  return (
    <div aria-hidden="true" className="flex flex-col pb-2">
      <div className="flex h-9 items-center gap-2">
        {back ? <Skeleton className="size-8 rounded-sm" /> : null}
        <Skeleton className="h-6 w-64 max-w-full rounded-sm" />
      </div>
      {sub ? (
        <div className={cn('flex h-7 items-end', back && 'pl-10')}>
          <Skeleton className="h-3 w-80 max-w-full rounded-xs" />
        </div>
      ) : null}
    </div>
  )
}
